import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Stack,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import FavoriteIcon from '@mui/icons-material/Favorite';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';

const DonatePage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const cardBaseStyles = {
    position: 'relative',
    borderRadius: '18px',
    border: '1px solid rgba(11,31,26,0.16)',
    background:
      'linear-gradient(145deg, rgba(255,255,255,0.98), rgba(14,145,140,0.08))',
    boxShadow: '0 12px 30px rgba(15,23,42,0.10)',
    p: { xs: 2.75, md: 3 },
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    textAlign: 'center',
    transition:
      'transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease',
    cursor: 'pointer',
    textDecoration: 'none',
    color: theme.palette.text.primary,
    '&:hover': {
      transform: 'translateY(4px)', // push down
      boxShadow: '0 18px 40px rgba(15,23,42,0.16)',
      borderColor: 'rgba(14,145,140,0.7)',
    },
  };

  return (
    <Box
      component="section"
      sx={{
        backgroundColor: theme.palette.background.default,
        // minHeight: '100vh',
        display: 'flex',
        alignItems: 'flex-start',
        pt: { xs: 4, md: 4.5 },
        pb: { xs: 5.5, md: 6.5 },
      }}
    >
      <Container maxWidth="md">
        {/* Heading + Mission */}
        <Box textAlign="center" mb={3}>
          <Typography
            variant={isMobile ? 'h6' : 'h5'}
            sx={{ fontWeight: 700, mb: 1.25 }}
          >
            Help Us Build the Civic Tools to Strengthen Democracy
          </Typography>
          <Typography
            variant="body1"
            sx={{
              maxWidth: 680,
              mx: 'auto',
              mb: 2,
              color: theme.palette.text.primary,
              fontSize: { xs: '0.95rem', md: '0.98rem' },
            }}
          >
            Your donation helps strengthen democracy by making civic action easier
            to take, elevating our collective voice, and keeping this platform
            free for all.
          </Typography>
        </Box>

        {/* One-Time Donation */}
        <Box display="flex" justifyContent="center" mb={{ xs: 4, md: 4.5 }}>
          <Box
            component="a"
            href="https://buy.stripe.com/4gM5kE9SZ5jj06k3Qu04800"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              ...cardBaseStyles,
              width: '100%',
              maxWidth: 310,
              alignItems: 'center',
            }}
          >
            <VolunteerActivismIcon
              sx={{ fontSize: 40, color: 'var(--primary-color)', mb: 1.25 }}
            />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                mb: 1.25,
                fontSize: { xs: '1.02rem', md: '1.06rem' },
              }}
            >
              Make a One-Time Donation
            </Typography>
            <Typography
              variant="body2"
              sx={{
                mb: 2.1,
                fontSize: { xs: '0.88rem', md: '0.9rem' },
              }}
            >
              Every contribution helps expand access and tools for civic
              participation.
            </Typography>
            <Button
              component="span"
              variant="contained"
              size="large"
              fullWidth
              sx={{
                backgroundColor: 'var(--primary-color)',
                color: '#fff',
                borderRadius: '999px',
                py: 1.05,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.98rem',
                boxShadow: '0 8px 18px rgba(15,23,42,0.18)',
                '&:hover': {
                  backgroundColor: 'var(--primary-color)',
                  boxShadow: '0 10px 24px rgba(15,23,42,0.24)',
                },
              }}
            >
              Donate Once
            </Button>
          </Box>
        </Box>

        {/* Monthly Supporters */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          mb={2.75}
          sx={{ gap: 1.1 }}
        >
          <Typography
            variant={isMobile ? 'h6' : 'h5'}
            sx={{
              fontWeight: 600,
              textAlign: 'center',
              fontSize: { xs: '1rem', md: '1.08rem' },
            }}
          >
            Become a Monthly Supporter
          </Typography>
          <FavoriteIcon sx={{ fontSize: 28, color: 'var(--primary-color)' }} />
        </Box>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 2.5, md: 3 }}
          justifyContent="center"
          alignItems="stretch"
        >
          {[
            {
              amount: 1,
              link: 'https://donate.stripe.com/aFa8wQ9SZcLL9GU0Ei04804',
            },
            {
              amount: 5,
              link: 'https://donate.stripe.com/4gMfZi9SZ6nn7yMfzc04801',
            },
            {
              amount: 10,
              link: 'https://donate.stripe.com/00w8wQd5bdPP2esdr404805',
            },
          ].map(({ amount, link }) => (
            <Box
              key={amount}
              component="a"
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                ...cardBaseStyles,
                flex: 1,
              }}
            >
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    mb: 1.25,
                    fontSize: { xs: '1rem', md: '1.04rem' },
                  }}
                >
                  Give ${amount}/month
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    mb: 2.1,
                    fontSize: { xs: '0.88rem', md: '0.9rem' },
                  }}
                >
                  Sustain this platform with a monthly contribution of ${amount}.
                </Typography>
              </Box>

              <Button
                component="span"
                variant="contained"
                size="large"
                fullWidth
                sx={{
                  backgroundColor: 'var(--primary-color)',
                  color: '#fff',
                  borderRadius: '999px',
                  py: 1.0,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  boxShadow: '0 7px 16px rgba(15,23,42,0.18)',
                  '&:hover': {
                    backgroundColor: 'var(--primary-color)',
                    boxShadow: '0 10px 24px rgba(15,23,42,0.24)',
                  },
                }}
              >
                Support ${amount}/mo
              </Button>
            </Box>
          ))}
        </Stack>
      </Container>
    </Box>
  );
};

export default DonatePage;
